import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Headphones, MessageCircle, RefreshCw, X } from 'lucide-react';
import ConversationThread from './ConversationThread';
import { createOrGetSupportConversation, fetchSupportUnreadCount } from './api';
import { socket, socketAuthPayload } from '../../admin/chat/socket';

const BUTTON_WIDTH = 86;
const BUTTON_HEIGHT = 56;
const BUTTON_MARGIN = 16;
const ALERT_STACK_OFFSET = 76;
const POLL_INTERVAL_MS = 30 * 1000;

function getViewportSize() {
  if (typeof window === 'undefined') return { width: 1024, height: 768 };
  return { width: window.innerWidth, height: window.innerHeight };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function supportScopeForRole(role) {
  if (role === 'driver') return 'driver';
  if (role === 'provider') return 'provider';
  if (role === 'customer') return 'store';
  return null;
}

function defaultPosition(viewport = getViewportSize()) {
  return {
    x: Math.max(BUTTON_MARGIN, viewport.width - BUTTON_WIDTH - BUTTON_MARGIN),
    y: Math.max(BUTTON_MARGIN, viewport.height - BUTTON_HEIGHT - BUTTON_MARGIN - ALERT_STACK_OFFSET),
  };
}

function clampPosition(position, viewport = getViewportSize()) {
  return {
    x: clamp(position.x, BUTTON_MARGIN, Math.max(BUTTON_MARGIN, viewport.width - BUTTON_WIDTH - BUTTON_MARGIN)),
    y: clamp(position.y, BUTTON_MARGIN, Math.max(BUTTON_MARGIN, viewport.height - BUTTON_HEIGHT - BUTTON_MARGIN)),
  };
}

function readStoredPosition(storageKey) {
  if (typeof window === 'undefined') return defaultPosition();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return defaultPosition();
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return defaultPosition();
    return clampPosition(parsed);
  } catch (_) {
    return defaultPosition();
  }
}

function compactError(err, fallback) {
  const payload = err?.response?.data;
  return payload?.message || payload?.error || err?.message || fallback;
}

export default function DirectSupportFloatingChat({ role, userId }) {
  const supportScope = supportScopeForRole(role);
  const enabled = Boolean(supportScope && userId);
  const storageKey = `fastMaxDirectSupportButtonPosition:${role || 'unknown'}:${userId || 'anon'}`;
  const [viewport, setViewport] = useState(getViewportSize);
  const [buttonPosition, setButtonPosition] = useState(() => readStoredPosition(storageKey));
  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dragStateRef = useRef(null);
  const didDragRef = useRef(false);
  const unreadFetchRef = useRef(false);

  const roomPayload = useMemo(() => socketAuthPayload({
    scope: supportScope,
    user_id: userId,
  }), [supportScope, userId]);

  const refreshUnread = useCallback(async () => {
    if (!enabled || unreadFetchRef.current) return;
    try {
      unreadFetchRef.current = true;
      const data = await fetchSupportUnreadCount(supportScope, userId);
      setUnreadCount(Number(data?.unread_count || 0));
      if (data?.conversation_id) setConversationId(data.conversation_id);
    } catch (err) {
      console.error('Support unread count error:', err);
    } finally {
      unreadFetchRef.current = false;
    }
  }, [enabled, supportScope, userId]);

  useEffect(() => {
    if (!enabled) return undefined;
    const handleResize = () => {
      const nextViewport = getViewportSize();
      setViewport(nextViewport);
      setButtonPosition(prev => clampPosition(prev, nextViewport));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(buttonPosition));
    } catch (_) {
      // localStorage can be unavailable in private browsing or locked-down contexts.
    }
  }, [buttonPosition, enabled, storageKey]);

  useEffect(() => {
    if (!enabled) return undefined;
    refreshUnread();
    const intervalId = window.setInterval(refreshUnread, POLL_INTERVAL_MS);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshUnread();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, refreshUnread]);

  useEffect(() => {
    if (!enabled) return undefined;
    const joinRoom = () => socket.emit('join_communications_scope', roomPayload);
    const leaveRoom = () => socket.emit('leave_communications_scope', roomPayload);
    const refreshFromEvent = (payload) => {
      const nextConversation = payload?.conversation || null;
      if (!conversationId || Number(payload?.conversation_id || nextConversation?.id) === Number(conversationId)) {
        refreshUnread();
      }
    };
    const rejoin = () => {
      joinRoom();
      refreshUnread();
    };

    joinRoom();
    socket.on('connect', rejoin);
    socket.on('conversation_message_created', refreshFromEvent);
    socket.on('conversation_updated', refreshFromEvent);
    socket.on('conversation_read_updated', refreshFromEvent);
    return () => {
      socket.off('connect', rejoin);
      socket.off('conversation_message_created', refreshFromEvent);
      socket.off('conversation_updated', refreshFromEvent);
      socket.off('conversation_read_updated', refreshFromEvent);
      leaveRoom();
    };
  }, [conversationId, enabled, refreshUnread, roomPayload]);

  const openSupport = useCallback(async () => {
    if (!enabled || loading) return;
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const nextConversation = await createOrGetSupportConversation(supportScope, userId);
      setConversation(nextConversation);
      setConversationId(nextConversation?.id || null);
      window.setTimeout(refreshUnread, 900);
    } catch (err) {
      console.error('Direct support conversation error:', err);
      setError(compactError(err, 'No se pudo abrir Fast Max.'));
    } finally {
      setLoading(false);
    }
  }, [enabled, loading, refreshUnread, supportScope, userId]);

  const handleConversationLoaded = useCallback((nextConversation) => {
    if (nextConversation?.id) {
      setConversation(nextConversation);
      setConversationId(nextConversation.id);
      window.setTimeout(refreshUnread, 900);
    }
  }, [refreshUnread]);

  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      initialPosition: buttonPosition,
    };
    didDragRef.current = false;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;
    if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) didDragRef.current = true;
    setButtonPosition(clampPosition({
      x: dragState.initialPosition.x + deltaX,
      y: dragState.initialPosition.y + deltaY,
    }, viewport));
  };

  const handlePointerUp = (event) => {
    if (dragStateRef.current?.pointerId === event.pointerId) {
      dragStateRef.current = null;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  };

  const handleButtonClick = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    openSupport();
  };

  const activeConversationId = conversation?.id || conversationId;

  if (!enabled) return null;

  return (
    <>
      <button
        type="button"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClick={handleButtonClick}
        className="fixed z-[3990] flex h-14 w-[86px] touch-none select-none items-center justify-center gap-1 rounded-2xl bg-[#B1336E] px-2 text-white shadow-2xl hover:bg-[#9d2d62] cursor-grab active:cursor-grabbing"
        style={{ left: buttonPosition.x, top: buttonPosition.y }}
        title="Fast Max"
      >
        <Headphones className="h-5 w-5 flex-shrink-0" />
        <span className="text-[11px] font-black leading-tight">Fast<br />Max</span>
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-black text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[4200] pointer-events-none">
          <div className="absolute inset-0 bg-slate-950/20 pointer-events-auto" onClick={() => setOpen(false)} />
          <div className="absolute bottom-3 right-3 top-3 flex w-[min(430px,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl pointer-events-auto sm:bottom-5 sm:right-5 sm:top-auto sm:h-[min(680px,calc(100vh-2.5rem))]">
            <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#B1336E]/10 text-[#B1336E]">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-sm font-black text-slate-950">Fast Max</h2>
                  <p className="text-[11px] font-semibold text-slate-500">Soporte directo con administradores</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
                title="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {error && (
              <div className="m-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </div>
            )}

            {loading && !activeConversationId ? (
              <div className="flex flex-1 items-center justify-center gap-2 text-sm font-semibold text-slate-500">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Abriendo Fast Max...
              </div>
            ) : (
              <ConversationThread
                conversationId={activeConversationId}
                currentUserId={userId}
                currentUserType={supportScope}
                className="min-h-0 flex-1 rounded-none border-0"
                allowNotes={false}
                inputPlaceholder="Escribe a Fast Max..."
                inputHelperText="Mensaje interno directo para administradores de Fast Max. No envía WhatsApp."
                onConversationLoaded={handleConversationLoaded}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
